/**
 * AI CONTEXT
 * Purpose: API context + batch print queue orchestration/state.
 * Manages: print queue items, server status sync from evaluation docs, retry/remove/clear actions.
 * Exposes: startBatchPrint and drawer control state.
 * Used by: BatchPrintDrawer + evaluation class card print actions.
 * Refactor: isolate queue reducer/state transitions into dedicated store/reducer hook.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"

import { evaluationAtoms } from "#atoms"
import ApiHandler from "#classes/ApiHandler"
import Firebase from "#classes/Firebase"
import {
  EVALUATION_STATUS,
  EvaluationConstructor,
  PRINT_URL_FRESHNESS_MS,
} from "#customTypes/composedTypes/evaluationTypes"
import {
  DOC_TYPES,
  FirebaseBatchUpdate,
} from "#customTypes/composedTypes/firebaseTypes"
import { useSomeDocs } from "#hooks/jotai/useSubscription"
import type {
  PrintEvalItem,
  PrintEvalProps,
} from "#hooks/newHooks/printEvalTypes"

type ApiContextValue = {
  apiHandler: ApiHandler
  startBatchPrint: (evalDataArray: PrintEvalProps[], userId?: string) => void
  retryItem: (itemId: string) => Promise<void>
  removeItem: (itemId: string) => void
  closeBatchModal: () => void
  openBatchModal: () => void
  clearFinished: () => void
  printItems: PrintEvalItem[]
  isBatchModalOpen: boolean
}

const ApiContext = createContext<ApiContextValue | null>(null)

export const ApiContextProvider = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const api = useMemo(() => new ApiHandler(), [])

  // Batch print UI state
  const [printItems, setPrintItems] = useState<PrintEvalItem[]>([])
  const [isBatchModalOpen, setIsBatchModalOpen] = useState(false)

  // Subscribe to evaluation documents by their evaluation.id (not local queue item id)
  const memoizedIds = useMemo(
    () => printItems.map((p) => p.evaluation.id),
    [printItems]
  )

  const evaluations = useSomeDocs<EvaluationConstructor>(
    evaluationAtoms,
    DOC_TYPES.EVALUATIONS,
    memoizedIds
  )

  // Merge server printing fields (status, error, url, filename) into local print items
  // Also map legacy/alternate server status values (e.g. "done") to our canonical set
  const mapServerStatus = (
    serverStatus?: "printed" | "done" | "processing" | "error" | "pending"
  ): PrintEvalItem["status"] => {
    switch (serverStatus) {
      case "printed":
      case "done":
        return "printed"
      case "processing":
        return "processing"
      case "error":
        return "error"
      case "pending":
      default:
        return "pending"
    }
  }

  // Sync printing fields from server into local items
  useEffect(() => {
    if (!evaluations || evaluations.length === 0) return

    setPrintItems((prev) => {
      const byEvalId = new Map(prev.map((p) => [p.evaluation.id, p]))
      let changed = false
      evaluations.forEach((ev) => {
        const existing = byEvalId.get(ev.id)
        if (!existing) return
        const status = mapServerStatus(ev.printing?.status)
        const error = ev.printing?.error ?? undefined
        const url =
          typeof ev.printing?.url === "string" ? ev.printing?.url : undefined
        const filename =
          typeof ev.printing?.filename === "string"
            ? ev.printing?.filename
            : undefined
        const next: PrintEvalItem = {
          ...existing,
          status,
          error,
          url,
          filename,
        }
        if (
          next.status !== existing.status ||
          next.error !== existing.error ||
          next.url !== existing.url ||
          next.filename !== existing.filename
        ) {
          byEvalId.set(ev.id, next)
          changed = true
        }
      })
      return changed ? Array.from(byEvalId.values()) : prev
    })
  }, [evaluations])

  // Auto-download fresh URLs at the context level (single source of truth)
  const downloadedIdsRef = useRef<Set<string>>(new Set())
  useEffect(() => {
    evaluations?.forEach((ev) => {
      const url = ev.printing?.url
      const filename = ev.printing?.filename || "evaluation.pdf"
      if (typeof url === "string" && !downloadedIdsRef.current.has(ev.id)) {
        downloadedIdsRef.current.add(ev.id)
        api.downloadFileFromUrl(url, filename).catch(() => {
          downloadedIdsRef.current.delete(ev.id)
        })
      }
    })
  }, [evaluations, api])

  // Actions
  const openBatchModal = useCallback(() => {
    setIsBatchModalOpen(true)
  }, [])

  const closeBatchModal = useCallback(() => {
    setIsBatchModalOpen(false)
  }, [])

  const removeItem = useCallback((itemId: string) => {
    setPrintItems((prev) => prev.filter((item) => item.id !== itemId))
  }, [])

  const clearFinished = useCallback(() => {
    // Remove downloaded IDs for items being cleared to avoid unbounded growth
    // and to allow future re-downloads if the same evaluation is reprinted.
    printItems
      .filter((p) => p.status === "printed")
      .forEach((p) => {
        downloadedIdsRef.current.delete(p.evaluation.id)
      })
    setPrintItems((prev) => {
      return prev.filter((p) => p.status !== "printed")
    })
  }, [printItems])

  const processBatchItems = useCallback(
    async (items: PrintEvalItem[], userId?: string) => {
      const now = Date.now()

      // Clear download tracking so new (or reused) URLs will be picked up
      items.forEach((i) => downloadedIdsRef.current.delete(i.evaluation.id))

      const hasFreshUrl = (item: PrintEvalItem) => {
        const { evaluation } = item
        if (evaluation.status !== EVALUATION_STATUS.PRINTED) return false
        const url = evaluation.printing?.url
        const ts = evaluation.printedAt ?? evaluation.lastUpdatedAt
        if (typeof url !== "string" || !ts) return false
        return now - new Date(ts).getTime() < PRINT_URL_FRESHNESS_MS
      }

      const freshItems = items.filter(hasFreshUrl)
      const staleItems = items.filter((i) => !hasFreshUrl(i))

      // Fresh URLs: download directly, mark as printed
      for (const item of freshItems) {
        const url = item.evaluation.printing!.url!
        const filename = item.evaluation.printing?.filename || "evaluation.pdf"
        downloadedIdsRef.current.add(item.evaluation.id)
        setPrintItems((prev) =>
          prev.map((p) =>
            p.evaluation.id === item.evaluation.id
              ? { ...p, status: "printed", url, filename }
              : p
          )
        )
        api.downloadFileFromUrl(url, filename).catch(() => {
          downloadedIdsRef.current.delete(item.evaluation.id)
          setPrintItems((prev) =>
            prev.map((p) =>
              p.evaluation.id === item.evaluation.id
                ? {
                    ...p,
                    status: "error",
                    error: "Download failed (expired URL)",
                  }
                : p
            )
          )
        })
      }

      // Stale items: reset printing fields and send to server for regeneration
      if (staleItems.length > 0) {
        const staleIds = new Set(staleItems.map((i) => i.evaluation.id))
        setPrintItems((prev) =>
          prev.map((p) =>
            staleIds.has(p.evaluation.id) ? { ...p, status: "pending" } : p
          )
        )

        try {
          const evaluationIds = staleItems.map((i) => i.evaluation.id)

          const isoNow = new Date().toISOString()
          const batchUpdate: FirebaseBatchUpdate = {
            [DOC_TYPES.EVALUATIONS]: evaluationIds.reduce(
              (acc, id) => {
                acc[id] = {
                  printing: {
                    status: "pending",
                    error: null,
                    url: null,
                    filename: null,
                  },
                  lastUpdatedAt: isoNow,
                }
                return acc
              },
              {} as Record<string, Partial<EvaluationConstructor>>
            ),
          }

          await Firebase.getInstance().updateDocuments(batchUpdate)
          await api.generateEvalBatchByIds(evaluationIds, userId)
        } catch (error: unknown) {
          console.error("❌ Failed to start server batch:", error)

          setPrintItems((prev) =>
            prev.map((p) =>
              staleItems.some((s) => s.evaluation.id === p.evaluation.id)
                ? {
                    ...p,
                    status: "error" as const,
                    error: (error as Error)?.message || "Failed to start batch",
                  }
                : p
            )
          )
        }
      }
    },
    [api]
  )

  const startBatchPrint = useCallback(
    (evalDataArray: PrintEvalProps[], userId?: string) => {
      const incoming: PrintEvalItem[] = evalDataArray.map((data, index) => ({
        ...data,
        id: `${data.studentName}-${data.evaluation.id}-${index}`,
        status: "pending" as const,
      }))

      // Merge with existing items, dedupe by evaluation.id, keep old items
      setPrintItems((prev) => {
        const byEvalId = new Map<string, PrintEvalItem>()
        // seed with previous
        prev.forEach((p) => byEvalId.set(p.evaluation.id, p))
        // merge incoming (overwrite or add), force pending for these
        incoming.forEach((n) => {
          const existing = byEvalId.get(n.evaluation.id)
          if (existing) {
            byEvalId.set(n.evaluation.id, {
              ...existing,
              studentName: n.studentName,
              evaluation: n.evaluation,
              traitsById: n.traitsById,
              status: "pending",
              error: undefined,
              url: undefined,
              filename: undefined,
            })
          } else {
            byEvalId.set(n.evaluation.id, n)
          }
        })
        return Array.from(byEvalId.values())
      })
      setIsBatchModalOpen(true)
      void processBatchItems(incoming, userId)
    },
    [processBatchItems]
  )

  const retryItem = useCallback(
    async (itemId: string) => {
      const item = printItems.find((p) => p.id === itemId)
      if (!item) return

      // Mark local item as processing
      setPrintItems((prev) =>
        prev.map((p) =>
          p.id === itemId ? { ...p, status: "processing", error: undefined } : p
        )
      )

      try {
        const { url, filename } = await api.generateEvalPdfByEvalId(
          item.evaluation.id
        )

        const now = new Date().toISOString()
        await Firebase.getInstance().updateDocument(
          DOC_TYPES.EVALUATIONS,
          item.evaluation.id,
          {
            printing: {
              status: "printed",
              url,
              filename,
              error: null,
            },
            lastUpdatedAt: now,
          }
        )

        await api.downloadFileFromUrl(url, filename)

        setPrintItems((prev) =>
          prev.map((p) =>
            p.id === itemId ? { ...p, status: "printed", url, filename } : p
          )
        )
      } catch (error: unknown) {
        const message = (error as Error)?.message || "Retry failed"
        const now = new Date().toISOString()
        await Firebase.getInstance().updateDocument(
          DOC_TYPES.EVALUATIONS,
          item.evaluation.id,
          {
            printing: {
              status: "error",
              error: message,
            },
            lastUpdatedAt: now,
          }
        )

        setPrintItems((prev) =>
          prev.map((p) =>
            p.id === itemId ? { ...p, status: "error", error: message } : p
          )
        )
      }
    },
    [api, printItems]
  )

  // Merge ApiHandler instance with UI state/actions. We attach fields to the instance
  // to preserve existing call sites that destructure ApiHandler methods directly.
  const value: ApiContextValue = {
    apiHandler: api,
    // Attach UI fields (non-enumerable-ness is irrelevant for context usage)
    startBatchPrint,
    retryItem,
    removeItem,
    closeBatchModal,
    openBatchModal,
    clearFinished,
    printItems,
    isBatchModalOpen,
  }

  return <ApiContext.Provider value={value}>{children}</ApiContext.Provider>
}

export const useApiContext = () => {
  const api = useContext(ApiContext)
  if (!api) {
    throw new Error("ApiContext not found")
  }
  return api
}
